import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, Receipt, ShieldCheck } from 'lucide-react';
import { API } from '@/lib/api/_client';
import { toast } from 'sonner';

const money = (v) => `A$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Compact card that shows the retainer status of a matter and lets the RMA
// one-click generate a top-up invoice for the exact shortfall.
export default function RetainerCard({ matterId, onInvoiceCreated }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    API.post(`/matters/${matterId}/retainer-shortfall`).then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [matterId]);
  React.useEffect(load, [load]);

  const topup = async () => {
    setCreating(true);
    try {
      const r = await API.post(`/matters/${matterId}/retainer-topup`);
      toast.success(`Top-up invoice created: ${money(r.data.shortfall)}`);
      onInvoiceCreated?.(r.data.invoice);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate invoice');
    } finally { setCreating(false); }
  };

  if (loading) return null;
  if (!data) return null;

  const short = data.shortfall > 0;
  return (
    <Card className={`border-l-4 ${short ? 'border-l-amber-500 bg-amber-50/30' : 'border-l-emerald-500 bg-emerald-50/30'}`} data-testid="retainer-card">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          {short ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
          <div className="font-serif text-base font-semibold text-[#1A3A5C]">
            {short ? 'Retainer top-up recommended' : 'Retainer coverage healthy'}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-xs" data-testid="retainer-numbers">
          <div><div className="text-slate-500">Trust balance</div><div className="font-semibold text-[#1A3A5C]">{money(data.trust_balance)}</div></div>
          <div><div className="text-slate-500">Unbilled WIP</div><div className="font-semibold text-[#1A3A5C]">{money(data.unbilled_wip)}</div></div>
          <div><div className="text-slate-500">DHA estimate</div><div className="font-semibold text-[#1A3A5C]">{money(data.dha_estimate)}</div></div>
        </div>
        {short && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-white p-2">
            <div className="text-sm">
              <span className="text-slate-500">Shortfall:</span>{' '}
              <span className="font-semibold text-amber-700" data-testid="retainer-shortfall-value">{money(data.shortfall)}</span>
            </div>
            <Button size="sm" onClick={topup} disabled={creating} data-testid="retainer-topup-btn">
              {creating ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Receipt className="mr-2 h-3 w-3" />}Generate top-up invoice
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
